// products.utils.ts
'use client'

import { useEffect, RefObject } from 'react'
import { useSearchParams } from 'next/navigation'
import { useSelector } from 'react-redux'
import { IRedux } from '@/_store/types'
import { ICategory } from '@/_types'
import { FEATURED_BRANDS } from '@/_utils/Constants'
import { PRODUCT_FIELDS } from './constants'

export { PRODUCT_FIELDS }

// Type definitions

export interface MenuContext {
  platform_id: string
  effectiveMenuId?: string
  deliveryType: string
  is_hall: string
}

export interface InfiniteScrollOptions {
  threshold: number
}

export interface FilteredQueryParams {
  tag_ids?: string
  sort?: string
  effectiveMenuId?: string
  deliveryType?: string
  category_id?: string
  limit?: number
}

export interface FilterParams {
  tagIds?: string
  sort?: string
  categoryId?: string
  isFiltered: boolean
}

/**
 * Custom hook to manage filter parameters
 */
export const useFilterParams = (): FilterParams => {
  const searchParams = useSearchParams()
  const tagIds = searchParams.get('tag_ids') || undefined
  const sort = searchParams.get('sort') || undefined

  const isFiltered = Boolean(tagIds || sort)

  return { tagIds, sort, isFiltered }
}

/**
 * Custom hook to get menu ID and delivery type from Redux state
 */
export const useMenuContext = (): MenuContext => {
  const { branches } = useSelector((state: IRedux) => state.common)
  const { platform_id, is_hall, order_type } = useSelector(
    (state: IRedux) => state.session
  )

  const shipperId = FEATURED_BRANDS['yaponamama']
  const branch = branches?.[shipperId]

  const deliveryType = order_type?.type || ''

  return {
    platform_id,
    effectiveMenuId: branch?.menu_id || undefined,
    deliveryType,
    is_hall,
  }
}

/**
 * Custom hook for infinite scroll
 */
export const useInfiniteScroll = (
  ref: RefObject<HTMLElement | null>,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  fetchNextPage: () => void,
  options: InfiniteScrollOptions = { threshold: 0.5 }
): void => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    }, options)

    const currentRef = ref.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
      observer.disconnect()
    }
  }, [ref, fetchNextPage, hasNextPage, isFetchingNextPage, options.threshold])
}

/**
 * Helper to flatten categories with their children
 */
export const flattenCategoriesWithChildren = (
  categories: ICategory[] = []
): ICategory[] => {
  return categories.flatMap((item) => [
    item,
    ...(item?.child_categories?.map((child) => ({
      ...(child as any),
      is_child: true,
    })) || []),
  ])
}

/**
 * Format filtered query parameters for API call
 */
export const getProductQueryParams = (
  page: number = 1,
  params: FilteredQueryParams
): Record<string, any> => ({
  page,
  limit: params?.limit || 28,
  fields: PRODUCT_FIELDS.join(','),
  product_types: 'origin,simple,combo',
  sort: params?.sort || undefined,
  category_id: params?.category_id || undefined,
  menu_id: params.effectiveMenuId,
  delivery_type: params.deliveryType,
  tag_ids: params?.tag_ids || undefined,
})
